/**
 * Security utility functions for authentication and session management
 *
 * IMPORTANT SECURITY NOTE:
 * JWT validation functions in this file are for CLIENT-SIDE UX PURPOSES ONLY.
 * They provide:
 * - Format validation for better error handling
 * - Expiry checking for proactive session management
 * - Session warnings and auto-refresh triggers
 *
 * SECURITY RESPONSIBILITY:
 * - All JWT signature verification MUST happen on the backend
 * - Never trust client-side JWT validation for security decisions
 * - Backend APIs must independently verify every token
 */

/**
 * Securely clear sensitive data from memory
 * @param obj - Object containing sensitive data
 */
export const secureClear = (obj: Record<string, any>): void => {
  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === 'string') {
      // Overwrite string with random data before deletion
      obj[key] = Math.random().toString(36).repeat(obj[key].length);
    }
    delete obj[key];
  });
};

/**
 * Generate a secure random string for CSRF tokens or state parameters
 * @param length - Length of the random string
 * @returns Secure random string
 */
export const generateSecureRandom = (length: number = 32): string => {
  if (
    typeof window !== 'undefined' &&
    window.crypto &&
    window.crypto.getRandomValues
  ) {
    const array = new Uint8Array(length);
    window.crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join(
      ''
    );
  }

  // Fallback for environments without crypto API
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

/**
 * Validate token format for client-side UX purposes only
 * WARNING: This is NOT a security validation - signature verification must happen on backend
 * @param token - JWT token to validate format only
 * @returns Boolean indicating if token format appears valid (for UX purposes)
 */
export const isValidTokenFormat = (token: string): boolean => {
  // Basic null/type check
  if (!token || typeof token !== 'string' || token.trim() === '') {
    return false;
  }

  // Basic JWT format check (3 parts separated by dots)
  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }

  // Check if each part exists and has reasonable length
  return parts.every((part) => part && part.length > 0);
};

/**
 * Extract expiry time from JWT token for client-side session management
 * WARNING: This reads unverified payload data - use only for UX purposes (session warnings, auto-refresh)
 * Backend must always verify token signature and expiry independently
 * @param token - JWT token
 * @returns Expiry timestamp or null if invalid (for UX purposes only)
 */
export const getTokenExpiry = (token: string): number | null => {
  if (!isValidTokenFormat(token)) {
    return null;
  }

  try {
    // Reading payload without signature verification - UX purposes only
    const payload = JSON.parse(atob(token.split('.')[1]!));
    return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
  } catch {
    return null;
  }
};

/**
 * Check if token appears expired based on unverified payload (UX purposes only)
 * WARNING: This is NOT a security check - backend must verify token validity independently
 * Used for proactive session management, refresh warnings, and better UX
 * @param token - JWT token
 * @param bufferTime - Buffer time in milliseconds (default: 5 minutes)
 * @returns Boolean indicating if token appears expired (for UX purposes only)
 */
export const isTokenExpired = (
  token: string,
  bufferTime: number = 5 * 60 * 1000
): boolean => {
  const expiry = getTokenExpiry(token);
  if (!expiry) {
    return true; // Consider invalid tokens as expired
  }

  return Date.now() >= expiry - bufferTime;
};

/**
 * Sanitize user input to prevent XSS attacks
 * @param input - User input string
 * @returns Sanitized string
 */
export const sanitizeInput = (input: string): string => {
  if (!input || typeof input !== 'string') {
    return '';
  }

  return input.replace(/[<>"'&]/g, (match) => {
    const entities: Record<string, string> = {
      '<': '<',
      '>': '>',
      '"': '&quot;',
      "'": '&#x27;',
      '&': '&amp;',
    };
    return entities[match] || match;
  });
};

/**
 * Create a stable session fingerprint for additional security
 * This fingerprint should remain consistent for the same browser/device
 * IMPORTANT: Removed dynamic elements (sessionId, timestamps) that change on each call
 * @returns Session fingerprint string
 */
export const createSessionFingerprint = (): string => {
  if (typeof window === 'undefined') {
    // Server-side fallback
    return 'server-side-fallback';
  }

  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    let canvasFingerprint = '';

    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Fingerprint test', 2, 2);
      canvasFingerprint = canvas.toDataURL();
    }

    // Stable fingerprint with consistent browser characteristics
    // REMOVED: sessionId (was causing different fingerprints each time)
    const fingerprint = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages?.join(',') || '',
      platform: navigator.platform,
      // Use fixed screen dimensions to avoid resize issues
      screenWidth: screen.width,
      screenHeight: screen.height,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      canvas: canvasFingerprint,
      colorDepth: screen.colorDepth,
      pixelDepth: screen.pixelDepth,
      // Remove availWidth/availHeight as they can change with window resize
      hardwareConcurrency: navigator.hardwareConcurrency || 0,
      maxTouchPoints: navigator.maxTouchPoints || 0,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack || 'unspecified',
      webgl: getWebGLFingerprint(),
      plugins: getPluginsFingerprint(),
    };

    // Create hash of fingerprint data
    const fingerprintString = JSON.stringify(fingerprint);
    const hash = simpleHash(fingerprintString);
    return btoa(hash).substring(0, 32);
  } catch {
    // Stable fallback fingerprint (removed random elements)
    const fallbackData = {
      userAgent: navigator.userAgent || 'unknown',
      platform: navigator.platform || 'unknown',
      language: navigator.language || 'unknown',
    };
    const fallbackHash = simpleHash(JSON.stringify(fallbackData));
    return btoa(fallbackHash).substring(0, 32);
  }
};

/**
 * Get WebGL fingerprint for enhanced security
 * @returns WebGL fingerprint string
 */
const getWebGLFingerprint = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const gl =
      (canvas.getContext('webgl') as WebGLRenderingContext | null) ||
      (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null);

    if (!gl) return 'no-webgl';

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return 'no-debug-info';

    const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || '';
    const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '';

    return `${vendor}-${renderer}`;
  } catch {
    return 'webgl-error';
  }
};

/**
 * Get plugins fingerprint
 * @returns Plugins fingerprint string
 */
const getPluginsFingerprint = (): string => {
  try {
    if (!navigator.plugins) return 'no-plugins';

    const plugins = Array.from(navigator.plugins)
      .map((plugin) => `${plugin.name}-${plugin.filename || 'unknown'}`)
      .sort()
      .join(',');

    return plugins.substring(0, 100); // Limit length
  } catch {
    return 'plugins-error';
  }
};

// Removed generateSessionId function as it's no longer used in fingerprinting

/**
 * Create a stable browser fingerprint for session validation
 * This version excludes all dynamic/random elements to ensure consistency
 *
 * Throws on generation failure rather than returning a fallback hash — a
 * fallback computed from less input (just userAgent) would silently diverge
 * from a later successful generation for the same browser, breaking BFF-side
 * fingerprint validation on refresh. Callers (login) should let this reject
 * the login attempt rather than proceed with a degraded fingerprint.
 * @returns Stable browser fingerprint string
 */
export const createStableBrowserFingerprint = (): string => {
  if (typeof window === 'undefined') {
    return 'server-side-fallback';
  }

  try {
    // Only include stable browser characteristics
    const stableFingerprint = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages?.join(',') || '',
      platform: navigator.platform,
      screenWidth: screen.width,
      screenHeight: screen.height,
      colorDepth: screen.colorDepth,
      pixelDepth: screen.pixelDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      hardwareConcurrency: navigator.hardwareConcurrency || 0,
      maxTouchPoints: navigator.maxTouchPoints || 0,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack || 'unspecified',
      webgl: getWebGLFingerprint(),
      // Simplified plugins fingerprint (more stable)
      pluginsCount: navigator.plugins ? navigator.plugins.length : 0,
    };

    const fingerprintString = JSON.stringify(stableFingerprint);
    const hash = simpleHash(fingerprintString);
    return btoa(hash).substring(0, 32);
  } catch (error) {
    throw new Error('Failed to generate browser fingerprint', {
      cause: error,
    });
  }
};

/**
 * Simple hash function for fingerprint data
 * @param str - String to hash
 * @returns Hash string
 */
const simpleHash = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString(36);
};

/**
 * Validate session fingerprint with enhanced security checks
 * @param storedFingerprint - Previously stored fingerprint
 * @param currentFingerprint - Current session fingerprint
 * @param allowPartialMatch - Whether to allow partial matches for some browser changes
 * @returns Boolean indicating if fingerprints match
 */
export const validateSessionFingerprint = (
  storedFingerprint: string,
  currentFingerprint: string,
  allowPartialMatch: boolean = false
): boolean => {
  if (!storedFingerprint || !currentFingerprint) {
    return false;
  }

  // Exact match is always valid
  if (storedFingerprint === currentFingerprint) {
    return true;
  }

  // If partial match is allowed, check for minor differences
  if (allowPartialMatch) {
    // Allow for minor browser changes (e.g., window resize, plugin updates)
    const similarity = calculateSimilarity(
      storedFingerprint,
      currentFingerprint
    );
    return similarity >= 0.8; // 80% similarity threshold
  }

  return false;
};

/**
 * Calculate similarity between two fingerprints
 * @param fp1 - First fingerprint
 * @param fp2 - Second fingerprint
 * @returns Similarity score between 0 and 1
 */
const calculateSimilarity = (fp1: string, fp2: string): number => {
  if (fp1 === fp2) return 1;

  const len1 = fp1.length;
  const len2 = fp2.length;
  const maxLen = Math.max(len1, len2);

  if (maxLen === 0) return 1;

  let matches = 0;
  const minLen = Math.min(len1, len2);

  for (let i = 0; i < minLen; i++) {
    if (fp1[i] === fp2[i]) {
      matches++;
    }
  }

  return matches / maxLen;
};

/**
 * Check if session fingerprint indicates potential security threat
 * @param storedFingerprint - Previously stored fingerprint
 * @param currentFingerprint - Current session fingerprint
 * @returns Security threat assessment
 */
export const assessFingerprintSecurity = (
  storedFingerprint: string,
  currentFingerprint: string
): {
  isSecure: boolean;
  threatLevel: 'none' | 'low' | 'medium' | 'high';
  reason: string;
} => {
  if (!storedFingerprint || !currentFingerprint) {
    return {
      isSecure: false,
      threatLevel: 'high',
      reason: 'Missing fingerprint data',
    };
  }

  if (storedFingerprint === currentFingerprint) {
    return {
      isSecure: true,
      threatLevel: 'none',
      reason: 'Exact fingerprint match',
    };
  }

  const similarity = calculateSimilarity(storedFingerprint, currentFingerprint);

  if (similarity >= 0.9) {
    return {
      isSecure: true,
      threatLevel: 'low',
      reason: 'Minor browser changes detected',
    };
  } else if (similarity >= 0.7) {
    return {
      isSecure: false,
      threatLevel: 'medium',
      reason: 'Moderate fingerprint changes detected',
    };
  } else {
    return {
      isSecure: false,
      threatLevel: 'high',
      reason: 'Significant fingerprint changes - possible session hijacking',
    };
  }
};

/**
 * Enhanced security configuration constants
 * Note: These are client-side UX configurations, not security enforcement
 */
export const SECURITY_CONFIG = {
  TOKEN_REFRESH_BUFFER: 5 * 60 * 1000, // 5 minutes - UX buffer for proactive refresh
  SESSION_WARNING_TIME: 5 * 60 * 1000, // 5 minutes - When to show session expiry warning
  MAX_LOGIN_ATTEMPTS: 5, // UI-side attempt tracking (backend enforces actual limits)
  LOCKOUT_DURATION: 15 * 60 * 1000, // 15 minutes - UI lockout duration
  PASSWORD_MIN_LENGTH: 8, // UI validation (backend enforces actual requirements)
  SESSION_TIMEOUT: 24 * 60 * 60 * 1000, // 24 hours - UI session timeout

  // Token format validation
  MIN_TOKEN_LENGTH: 10, // Minimum reasonable token length
  EXPECTED_JWT_PARTS: 3, // JWT should have 3 parts (header.payload.signature)

  // Session fingerprinting
  FINGERPRINT_SIMILARITY_THRESHOLD: 0.8, // Minimum similarity for partial matches
  FINGERPRINT_VALIDATION_ENABLED: true, // Enable fingerprint validation

  // Session storage
  SESSION_STORAGE_KEY: 'auth-session-data',
  TEMP_AUTH_KEY: 'temp-auth',
  SESSION_DATA_MAX_AGE: 60 * 60 * 1000, // 1 hour - Max age for session data

  // Automatic refresh
  AUTO_REFRESH_ENABLED: true, // Enable automatic token refresh
  AUTO_REFRESH_RETRY_COUNT: 3, // Number of retry attempts for failed refresh
  AUTO_REFRESH_RETRY_DELAY: 1000, // Delay between retry attempts (ms)

  // Security monitoring
  SECURITY_MONITORING_ENABLED: true, // Enable security event monitoring
  FAILED_REFRESH_THRESHOLD: 3, // Max failed refresh attempts before logout
  SUSPICIOUS_ACTIVITY_THRESHOLD: 5, // Max suspicious activities before enhanced security
} as const;

/**
 * Security event types for monitoring
 */
export const SECURITY_EVENTS = {
  FINGERPRINT_MISMATCH: 'fingerprint_mismatch',
  TOKEN_REFRESH_FAILED: 'token_refresh_failed',
  SESSION_HIJACK_ATTEMPT: 'session_hijack_attempt',
  MULTIPLE_TAB_ACCESS: 'multiple_tab_access',
  SUSPICIOUS_LOGIN_PATTERN: 'suspicious_login_pattern',
} as const;

/**
 * Log security events for monitoring
 * @param _event - Security event type
 * @param _details - Additional event details
 */
export const logSecurityEvent = (
  _event: string,
  _details: Record<string, any> = {}
): void => {
  if (!SECURITY_CONFIG.SECURITY_MONITORING_ENABLED) return;

  // In a production environment, you would send this to your security monitoring service
  // Example: sendToSecurityService({ event: _event, timestamp: new Date().toISOString(), userAgent: navigator.userAgent, url: window.location.href, ..._details });
};

export default {
  secureClear,
  generateSecureRandom,
  isValidTokenFormat,
  getTokenExpiry,
  isTokenExpired,
  sanitizeInput,
  createSessionFingerprint,
  createStableBrowserFingerprint,
  validateSessionFingerprint,
  assessFingerprintSecurity,
  logSecurityEvent,
  SECURITY_CONFIG,
  SECURITY_EVENTS,
};
